import { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameMonth,
  isSameDay,
  isAfter,
  endOfDay,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';

const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

const Popover = styled.div`
  position: absolute;
  top: 48px;
  right: 0;
  background: #18171c;
  color: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 16px #000a;
  padding: 12px;
  z-index: 100;
  min-width: 260px;
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const Arrow = styled.button`
  background: none;
  border: none;
  color: #b9b9c3;
  font-size: 1.2em;
  cursor: pointer;
`;

const DaysRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const DayCell = styled.div<{
  selected?: boolean;
  faded?: boolean;
  isStart?: boolean;
  isEnd?: boolean;
}>`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  margin: 1px;
  background: ${({ selected, isStart, isEnd }) =>
    isStart || isEnd ? '#a084e8' : selected ? '#2d1e4a' : 'none'};
  color: ${({ faded }) => (faded ? '#666' : '#fff')};
  border: ${({ isStart, isEnd, selected }) =>
    isStart || isEnd
      ? '2px solid #fff'
      : selected
      ? '1px solid #a084e8'
      : 'none'};
  cursor: pointer;
  &:hover {
    background: #23222a;
  }
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  color: #fff;
  margin-top: 8px;
  cursor: pointer;
  float: right;
  font-size: 1em;
  opacity: 0.7;
  &:hover {
    opacity: 1;
    text-decoration: underline;
  }
`;

const Weekdays = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

export const DatePickerPopover = ({
  value,
  onChange,
  onClear,
  onClose,
}: {
  value: { startDate: Date | null; endDate: Date | null };
  onChange: (range: { startDate: Date; endDate: Date }) => void;
  onClear: () => void;
  onClose: () => void;
}) => {
  const [currentMonth, setCurrentMonth] = useState(
    value.startDate || new Date()
  );
  const [range, setRange] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>(value);
  const ref = useRef<HTMLDivElement>(null);

  // Fecha ao clicar fora
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  function handleDayClick(day: Date) {
    if (!range.startDate || (range.startDate && range.endDate)) {
      setRange({ startDate: day, endDate: null });
    } else if (range.startDate && !range.endDate) {
      if (day < range.startDate) {
        setRange({ startDate: day, endDate: range.startDate });
      } else {
        setRange({ startDate: range.startDate, endDate: day });
      }
      onChange({
        startDate: day < range.startDate ? day : range.startDate,
        endDate: day < range.startDate ? range.startDate : day,
      });
    }
  }

  function renderDays() {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 0 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 0 });

    const rows = [];
    let days = [];
    let day = startDate;
    let formattedDate = '';

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        formattedDate = format(day, 'd');
        const isFaded = !isSameMonth(day, monthStart);
        const isFuture = isAfter(day, endOfDay(new Date()));
        const dayCopy = new Date(day); // Cópia imutável

        const isInRange = !!(
          range.startDate &&
          range.endDate &&
          day >= range.startDate &&
          day <= range.endDate
        );
        const isStart = range.startDate && isSameDay(day, range.startDate);
        const isEnd = range.endDate && isSameDay(day, range.endDate);

        days.push(
          <DayCell
            key={dayCopy.toString()}
            faded={!!isFaded || isFuture}
            selected={isInRange}
            isStart={!!isStart}
            isEnd={!!isEnd}
            onClick={() => {
              if (!isFuture) handleDayClick(dayCopy);
            }}
            style={isFuture ? { pointerEvents: 'none', opacity: 0.4 } : {}}
          >
            {formattedDate}
          </DayCell>
        );
        day = addDays(day, 1);
      }
      rows.push(<DaysRow key={day.toString()}>{days}</DaysRow>);
      days = [];
    }
    return rows;
  }

  return (
    <Popover ref={ref}>
      <CalendarHeader>
        <Arrow onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
          {'←'}
        </Arrow>
        <span style={{ fontWeight: 500 }}>
          {capitalizeFirstLetter(
            format(currentMonth, 'MMMM yyyy', { locale: ptBR })
          )}
        </span>
        <Arrow onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
          {'→'}
        </Arrow>
      </CalendarHeader>
      <DaysRow>
        {Weekdays.map((d, index) => (
          <DayCell
            as="span"
            key={`${d}_${index}`}
            style={{ fontWeight: 600, color: '#b9b9c3' }}
          >
            {d}
          </DayCell>
        ))}
      </DaysRow>
      {renderDays()}
      <ClearButton
        onClick={() => {
          setRange({ startDate: null, endDate: null });
          onClear();
        }}
      >
        Clear
      </ClearButton>
    </Popover>
  );
};
